import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/helpers";
import { Building2, Users2, ShieldCheck, Plus, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { OrgCard } from "@/components/OrgCard";
import { DeleteOrgDialog } from "@/components/DeleteOrgDialog";
import { FacilitiesHealth } from "@/components/FacilitiesHealth";
import { StripeHealth } from "@/components/StripeHealth";
import { ClientDialog } from "@/pages/super/Billing";
import { OrgModulesDialog, ModuleCatalogDialog } from "@/components/OrgModules";
import { DemoSandboxes } from "@/components/DemoSandboxes";

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [subs, setSubs] = useState({});
  const [manage, setManage] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [modsOf, setModsOf] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", code: "", plan: "essentiel", max_users: 19, admin_email: "", admin_password: "demo123", admin_first_name: "Admin", admin_last_name: "Entreprise" });
  const [error, setError] = useState("");

  const load = () => {
    api.get("/super/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/super/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/billing/overview").then((r) => setSubs(Object.fromEntries((r.data.rows || []).map((x) => [x.organization_id, x])))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setError("");
    try { await api.post("/super/organizations", f); toast.success("Entreprise créée"); setOpen(false); load(); }
    catch (e) { setError(apiError(e.response?.data?.detail) || e.message); }
  };
  const toggleV2 = async (o) => {
    if (!window.confirm(o.planning_v2_enabled ? `Désactiver Planning V2 pour ${o.name} ? (données conservées)` : `Activer Planning V2 pour ${o.name} uniquement ?`)) return;
    try { await api.patch(`/super/organizations/${o.id}`, { planning_v2_enabled: !o.planning_v2_enabled }); toast.success(o.planning_v2_enabled ? "Planning V2 désactivé (données conservées)" : "Planning V2 activé pour cette entreprise"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = async (o) => {
    try { await api.patch(`/super/organizations/${o.id}`, { active: !o.active }); toast.success(o.active ? "Entreprise archivée (données conservées)" : "Entreprise réactivée"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><h1 className="text-3xl font-heading font-extrabold text-brand-900">Entreprises clientes</h1><p className="text-slate-500 mt-1">Gestion des comptes, abonnements et état.</p></div>
          <div className="flex gap-2"><Button data-testid="module-catalog-button" variant="outline" onClick={() => setCatOpen(true)} className="h-11">Tarifs des modules</Button><Button data-testid="create-org-button" onClick={() => setOpen(true)} className="bg-accent-600 hover:bg-accent-700 h-11"><Plus className="w-4 h-4 mr-1.5" />Nouvelle entreprise</Button></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SuperStat icon={Building2} label="Entreprises" value={stats?.total_orgs} />
          <SuperStat icon={ShieldCheck} label="Actives" value={stats?.active_orgs} accent="text-green-600" />
          <SuperStat icon={Building2} label="Archivées" value={stats?.suspended_orgs} accent={stats?.suspended_orgs ? "text-slate-600" : "text-brand-900"} />
          <SuperStat icon={Users2} label="Utilisateurs" value={stats?.total_users} />
        </div>

        <FacilitiesHealth />
        <StripeHealth />
        <DemoSandboxes />
        {/* Mobile : cartes ; desktop/tablette large : tableau */}
        <div className="md:hidden space-y-3" data-testid="org-cards">
          {orgs.map((o) => <OrgCard key={o.id} org={o} sub={subs[o.id]} onManage={(org) => setManage(org.id)} onModules={setModsOf} onToggle={toggle} onDelete={setToDelete} />)}
          {orgs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Aucune entreprise.</p>}
        </div>
        <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">Entreprise</th><th className="px-4 py-3 font-semibold">Code</th><th className="px-4 py-3 font-semibold">Formule</th><th className="px-4 py-3 font-semibold">Utilisateurs</th><th className="px-4 py-3 font-semibold">Demandes</th><th className="px-4 py-3 font-semibold">Créée</th><th className="px-4 py-3 font-semibold">État</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {orgs.map((o) => (
                <tr key={o.id} data-testid={`org-row-${o.id}`} className={`hover:bg-slate-50 ${o.active ? "" : "bg-slate-50/70 text-slate-500"}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{o.name}</td>
                  <td className="px-4 py-3 font-mono text-slate-500">{o.code}</td>
                  <td className="px-4 py-3 capitalize">{subs[o.id]?.plan_name || o.plan}</td>
                  <td className="px-4 py-3">{o.user_count} / {o.max_users}</td>
                  <td className="px-4 py-3">{o.request_count}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3"><span data-testid={`org-state-${o.id}`} className={`text-xs font-semibold px-2 py-1 rounded-full border ${o.active ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-200 text-slate-700 border-slate-300"}`}>{o.active ? "Active" : "Archivée"}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button data-testid={`planning-v2-org-${o.id}`} variant="outline" size="sm" onClick={() => toggleV2(o)} className={o.planning_v2_enabled ? "border-emerald-300 text-emerald-800" : ""}>{o.planning_v2_enabled ? "Planning V2 activé" : "Activer Planning V2"}</Button>
                      <Button data-testid={`modules-org-${o.id}`} variant="outline" size="sm" onClick={() => setModsOf(o)}>Modules</Button>
                      <Button data-testid={`manage-org-${o.id}`} variant="outline" size="sm" onClick={() => setManage(o.id)}>Gérer</Button>
                      <Button data-testid={`toggle-org-${o.id}`} variant="outline" size="sm" onClick={() => toggle(o)} title={o.active ? "Archiver" : "Réactiver"}>{o.active ? <Archive className="w-4 h-4" /> : <ArchiveRestore className="w-4 h-4" />}</Button>
                      <Button data-testid={`delete-org-${o.id}`} variant="outline" size="sm" onClick={() => setToDelete(o)} title="Supprimer définitivement" className="text-red-700 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {modsOf && <OrgModulesDialog org={modsOf} onClose={() => { setModsOf(null); load(); }} />}
        {catOpen && <ModuleCatalogDialog onClose={() => setCatOpen(false)} />}
        {manage && <ClientDialog orgId={manage} onClose={() => { setManage(null); load(); }} />}
        {toDelete && <DeleteOrgDialog org={toDelete} onClose={() => setToDelete(null)} onDeleted={() => { setToDelete(null); load(); }} />}
        <p className="text-xs text-slate-500">Le super administrateur n'a pas accès aux dossiers patients des entreprises clientes.</p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Nouvelle entreprise</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nom</Label><Input data-testid="org-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 h-11" /></div>
              <div><Label>Code</Label><Input data-testid="org-code-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} className="mt-1 h-11 uppercase" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Formule</Label><select data-testid="org-plan" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} className="mt-1 h-11 w-full rounded-lg border border-slate-200 px-3"><option value="essentiel">Essentiel</option><option value="pro">Pro</option></select></div>
              <div><Label>Utilisateurs max</Label><Input type="number" data-testid="org-max-users" value={f.max_users} onChange={(e) => setF({ ...f, max_users: parseInt(e.target.value) || 1 })} className="mt-1 h-11" /></div>
            </div>
            <div><Label>Email admin</Label><Input type="email" data-testid="org-admin-email" value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>Mot de passe admin</Label><Input data-testid="org-admin-password" value={f.admin_password} onChange={(e) => setF({ ...f, admin_password: e.target.value })} className="mt-1 h-11" /></div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button data-testid="submit-org" onClick={create} className="bg-accent-600 hover:bg-accent-700">Créer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SuperStat({ icon: Icon, label, value, accent = "text-brand-900" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <Icon className="w-5 h-5 text-accent-600 mb-2" />
      <p className={`text-3xl font-heading font-extrabold ${accent}`}>{value ?? "…"}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
